import { useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import { useAgentPortalGuard } from "./useAgentPortalGuard.js";
import AgentPortalGuardState from "./AgentPortalGuardState.jsx";
import { useOwningAgentProfile } from "./useOwningAgentProfile.js";
import { createAgentContact, deleteAgentContact } from "./agentContactsApi.js";
import "./agentContacts.css";
import "../pages/states.css";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Agent", "Contacts"];

const CONTACT_TYPE_OPTIONS = [
  { value: "Phone", label: "โทรศัพท์" },
  { value: "WhatsApp", label: "WhatsApp" },
  { value: "Line", label: "LINE" },
];

const CONTACT_TYPE_LABELS_TH = {
  Phone: "โทรศัพท์",
  WhatsApp: "WhatsApp",
  Line: "LINE",
};

const EMPTY_FORM = { contactType: "Phone", contactValue: "", isPrimary: false };

function describeWriteError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 400) {
    return "กรุณากรอกประเภทและข้อมูลติดต่อให้ครบถ้วน";
  }
  return "ไม่สามารถบันทึกรายการนี้ได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * /backoffice/agent/contacts - Prompt 138's Agent Portal Contacts screen.
 * Reads from the same GET /api/backoffice/agents/{agentId}/profile response
 * already used by AgentOrgChartPage.jsx (useOwningAgentProfile.js) - the
 * `contacts[]` array on AgentProfileResponse. Add/remove call the real,
 * pre-existing POST/DELETE .../agents/{agentId}/contacts endpoints
 * (BackofficeController.CreateAgentContact/DeleteAgentContact) that already
 * shipped before Prompt 138 with no frontend caller anywhere in this
 * codebase - this page is the first UI to use them, not a new backend
 * route. Every write always targets the signed-in Agent's own agentId
 * (guard.agentId from findOwningAgentId) - never an id from a URL param or
 * another Agent's data.
 */
export default function AgentContactsPage() {
  const guard = useAgentPortalGuard();
  const agentId = guard.status === "ready" ? guard.agentId : null;
  const profile = useOwningAgentProfile(agentId);

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitState, setSubmitState] = useState({ status: "idle", error: null });
  const [deletingId, setDeletingId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [contacts, setContacts] = useState(null);

  if (guard.status !== "ready") {
    return <AgentPortalGuardState crumbs={CRUMBS} guard={guard} />;
  }

  const effectiveContacts = contacts ?? profile.data?.contacts ?? null;

  function handleAdd() {
    if (!form.contactValue.trim()) {
      setSubmitState({ status: "error", error: { message: "กรุณากรอกข้อมูลติดต่อ" } });
      return;
    }
    setSubmitState({ status: "submitting", error: null });
    createAgentContact(agentId, form)
      .then(() => {
        setSubmitState({ status: "success", error: null });
        setForm(EMPTY_FORM);
        setContacts(null);
        setReloadToken((token) => token + 1);
      })
      .catch((error) => setSubmitState({ status: "error", error }));
  }

  function handleDelete(contactId) {
    setDeletingId(contactId);
    deleteAgentContact(agentId, contactId)
      .then(() => {
        setContacts((current) => (current ?? profile.data?.contacts ?? []).filter((c) => c.contactId !== contactId));
        setDeletingId(null);
      })
      .catch(() => setDeletingId(null));
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-ac-page">
        <header className="wx-ac-head">
          <h1>ข้อมูลติดต่อของฉัน</h1>
          <p>ช่องทางติดต่อที่ผู้สมัครแรงงานจะเห็นในจดหมายเชิญและหน้าโปรไฟล์ของคุณ</p>
        </header>

        <section className="wx-panel wx-ac-list-panel" key={reloadToken}>
          <h2 className="wx-panel-title">ช่องทางติดต่อปัจจุบัน</h2>

          {profile.status === "loading" && effectiveContacts === null ? (
            <PageSkeleton variant="table" label="กำลังโหลดข้อมูลติดต่อ..." />
          ) : profile.status === "error" && effectiveContacts === null ? (
            <div className="wx-state-card wx-state-card--danger" role="alert">
              <p className="wx-state-body">ไม่สามารถโหลดข้อมูลติดต่อได้ กรุณาลองรีเฟรชหน้านี้</p>
            </div>
          ) : !effectiveContacts || effectiveContacts.length === 0 ? (
            <p className="wx-empty-list">ยังไม่มีช่องทางติดต่อ กรุณาเพิ่มด้านล่าง</p>
          ) : (
            <div className="wx-ac-contact-list">
              {effectiveContacts.map((contact) => (
                <div className="wx-ac-contact-row" key={contact.contactId}>
                  <span className="wx-ac-contact-type">{CONTACT_TYPE_LABELS_TH[contact.contactType] ?? contact.contactType}</span>
                  <span className="wx-ac-contact-value">{contact.contactValue}</span>
                  {contact.isPrimary && <span className="wx-badge wx-badge--info">หลัก</span>}
                  <button
                    type="button"
                    className="wx-button wx-button--ghost wx-ac-remove-button"
                    onClick={() => handleDelete(contact.contactId)}
                    disabled={deletingId === contact.contactId}
                  >
                    {deletingId === contact.contactId ? "กำลังลบ..." : "ลบ"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="wx-panel wx-ac-form-panel">
          <h2 className="wx-panel-title">เพิ่มช่องทางติดต่อ</h2>
          <div className="wx-ac-form-grid">
            <label className="wx-ac-field">
              <span>ประเภท</span>
              <select aria-label="contactType" value={form.contactType} onChange={(event) => setForm((prev) => ({ ...prev, contactType: event.target.value }))}>
                {CONTACT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="wx-ac-field wx-ac-field--span-2">
              <span>ข้อมูลติดต่อ</span>
              <input
                type="text"
                aria-label="contactValue"
                placeholder="เช่น 08X-XXX-XXXX หรือ LINE ID"
                value={form.contactValue}
                onChange={(event) => setForm((prev) => ({ ...prev, contactValue: event.target.value }))}
              />
            </label>
            <label className="wx-ac-field wx-ac-checkbox">
              <input type="checkbox" checked={form.isPrimary} onChange={(event) => setForm((prev) => ({ ...prev, isPrimary: event.target.checked }))} />
              <span>ตั้งเป็นช่องทางหลัก</span>
            </label>
          </div>

          {submitState.status === "error" && (
            <p className="wx-ac-form-message" role="alert">
              {describeWriteError(submitState.error)}
            </p>
          )}

          <div className="wx-ac-form-actions">
            <button type="button" className="wx-button wx-button--primary" onClick={handleAdd} disabled={submitState.status === "submitting"}>
              {submitState.status === "submitting" ? "กำลังเพิ่ม..." : "+ เพิ่มช่องทางติดต่อ"}
            </button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
